// src/context/FormDraftContext.tsx
//
// A sessionStorage-backed draft store that lets form pages save their state
// before navigating to a sub-page (e.g. Master Data management) and restore
// it on return.
//
// Uses sessionStorage so drafts survive:
//   ✅  React hot-reload
//   ✅  F5 / browser refresh while on the sub-page
//   ✅  Tab crash + restore (some browsers)
//
// Drafts are automatically cleared when the tab/session ends (sessionStorage
// semantics), so there is no risk of stale data showing up in a new session.
//
// Usage:
//   const { saveDraft, consumeDraft, peekDraft, clearDraft } = useFormDraft();
//
//   // Before navigating away:
//   saveDraft("expenditureForm", { formState: form });
//
//   // On mount after returning (reads and immediately clears the draft):
//   const draft = consumeDraft("expenditureForm");
//   if (draft) setForm(draft.formState);
//
//   // Check without clearing (e.g. to patch a field into an existing draft):
//   const existing = peekDraft("expenditureForm");
//   saveDraft("expenditureForm", { ...existing, newCategoryId: id });

import React, {
  createContext,
  useCallback,
  useContext,
} from "react";

const STORAGE_PREFIX = "__formDraft__";

interface FormDraftContextValue {
  /** Persist a draft under `key`. Overwrites any previous draft for that key. */
  saveDraft: (key: string, data: any) => void;
  /**
   * Read and immediately clear the draft stored under `key`.
   * Returns `null` when no draft exists.
   */
  consumeDraft: (key: string) => any | null;
  /** Read the draft without clearing it. Returns `null` when no draft exists. */
  peekDraft: (key: string) => any | null;
  /** Explicitly remove a draft without reading it. */
  clearDraft: (key: string) => void;
}

const FormDraftContext = createContext<FormDraftContextValue>({
  saveDraft: () => {},
  consumeDraft: () => null,
  peekDraft: () => null,
  clearDraft: () => {},
});

function storageKey(key: string) {
  return `${STORAGE_PREFIX}${key}`;
}

export function FormDraftProvider({ children }: { children: React.ReactNode }) {
  const saveDraft = useCallback((key: string, data: any) => {
    try {
      sessionStorage.setItem(storageKey(key), JSON.stringify(data));
    } catch {
      // sessionStorage may throw in private browsing with full quota.
      // Fail silently — the form will just not be restored.
    }
  }, []);

  const peekDraft = useCallback((key: string) => {
    try {
      const raw = sessionStorage.getItem(storageKey(key));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const consumeDraft = useCallback((key: string) => {
    try {
      const raw = sessionStorage.getItem(storageKey(key));
      if (!raw) return null;
      sessionStorage.removeItem(storageKey(key));
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  const clearDraft = useCallback((key: string) => {
    try {
      sessionStorage.removeItem(storageKey(key));
    } catch {
      // ignore
    }
  }, []);

  return (
    <FormDraftContext.Provider value={{ saveDraft, consumeDraft, peekDraft, clearDraft }}>
      {children}
    </FormDraftContext.Provider>
  );
}

export function useFormDraft() {
  return useContext(FormDraftContext);
}
